import { Transformer } from '@lexical/markdown';
import { Klass, LexicalNode } from 'lexical';
import { ReactNode, useEffect, useState } from 'react';

import { useFederalEstateTaxFormatted } from '@/modules/irs/useIRSConstants';

import { TextEditor, TextEditorProps } from '../TextEditor/TextEditor';

type MarkdownVars = 'FEDERAL_TAX_RATE_SENTINEL';

function useMarkdownVars(markdown: string) {
  const rate = useFederalEstateTaxFormatted();

  return markdown.replace(
    new RegExp(
      'FEDERAL_TAX_RATE_SENTINEL' as const satisfies MarkdownVars,
      'g'
    ),
    `${rate}%`
  );
}

interface MarkdownProps {
  markdown: string;
  EditorInner?: TextEditorProps['EditorInner'];
  TextEditorWrapper?: TextEditorProps['TextEditorWrapper'];
  innerSx?: TextEditorProps['innerSx'];
  preserveNewLines?: boolean;
  /** Extra Lexical node types to register with the editor. */
  additionalNodes?: Klass<LexicalNode>[];
  /** Extra markdown transformers, given priority over the defaults. See
   *  `TextEditor`. */
  additionalTransformers?: Transformer[];
  /** Extra plugins to render inside the LexicalComposer. */
  additionalPlugins?: ReactNode;
  /** Optional tenant-branded body text color. Forwarded to the Lexical
   *  theme. See `TextEditor`. */
  bodyTextColor?: TextEditorProps['bodyTextColor'];
  /** Optional tenant-branded body text alignment. Forwarded to the
   *  Lexical theme. See `TextEditor`. */
  bodyTextAlignment?: TextEditorProps['bodyTextAlignment'];
}

// Helper component to render markdown in a read-only text editor
export function Markdown({
  markdown: markdownExternal,
  EditorInner,
  TextEditorWrapper,
  innerSx,
  preserveNewLines = true,
  additionalNodes,
  additionalTransformers,
  additionalPlugins,
  bodyTextColor,
  bodyTextAlignment,
}: MarkdownProps) {
  const markdown = useMarkdownVars(markdownExternal);

  // get unique key when markdown changes
  const [key, setKey] = useState(0);

  // Remount the TextEditor when the markdown content, body text color, or
  // body text alignment changes. LexicalComposer reads its theme only once
  // at mount, so class-level overrides (generated by useTextEditorClasses)
  // are stale if these change after the initial render.
  useEffect(() => {
    setKey((prevKey) => prevKey + 1);
  }, [markdown, bodyTextColor, bodyTextAlignment]);

  return (
    <TextEditor
      key={key}
      initialValue={markdown}
      readOnly
      hideBorder
      EditorInner={EditorInner}
      TextEditorWrapper={TextEditorWrapper}
      innerSx={innerSx}
      preserveNewLines={preserveNewLines}
      additionalNodes={additionalNodes}
      additionalTransformers={additionalTransformers}
      additionalPlugins={additionalPlugins}
      bodyTextColor={bodyTextColor}
      bodyTextAlignment={bodyTextAlignment}
    />
  );
}
